import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchActivityImage } from '../api/api';

const PrintPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const fileUrl = searchParams.get('url');
    const [blobUrl, setBlobUrl] = useState(null);
    const [isPdf, setIsPdf] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const printedRef = useRef(false);

    useEffect(() => {
        const loadContent = async () => {
            if (!fileUrl) {
                setError("No URL provided");
                setLoading(false);
                return;
            }

            try {
                const isPdfCheck = fileUrl.toLowerCase().endsWith('.pdf');
                setIsPdf(isPdfCheck);

                const blob = await fetchActivityImage(fileUrl);
                const objectUrl = URL.createObjectURL(new Blob([blob], { type: isPdfCheck ? 'application/pdf' : 'image/jpeg' }));
                setBlobUrl(objectUrl);
                setLoading(false);
            } catch (err) {
                console.error("Error loading print content", err);
                setError("Failed to monitor content");
                setLoading(false);
            }
        };

        loadContent();

        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [fileUrl]);

    useEffect(() => {
        if (!loading && blobUrl && !printedRef.current) {
            const handleAfterPrint = () => {
                // Determine if we should navigate back automatically
                // Some browsers fire this immediately, some after dialog close
                // We'll trust it for now but the user has a manual button too
                navigate(-1);
            };

            const doPrint = () => {
                printedRef.current = true;

                // Browser specific quirks
                const userAgent = navigator.userAgent || navigator.vendor || window.opera;
                const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
                const isFirefox = typeof InstallTrigger !== 'undefined';
                const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);

                if (isSafari || isFirefox || isChrome) {
                    // Universal listener for standard compliant browsers
                    window.addEventListener("afterprint", handleAfterPrint);
                }

                setTimeout(() => {
                    try {
                        window.print();
                    } catch (e) {
                        console.error("Print failed", e);
                    }
                }, 800);
            };

            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

            if (!isMobile) {
                if (isPdf) {
                    // PDFs often need user interaction or don't support auto-print in iframes seamlessly across all mobile devices
                    // We will attempt to print, but the user might see the PDF viewer controls
                    // setTimeout(doPrint, 1500);
                } else {
                    setTimeout(doPrint, 500);
                }

                return () => {
                    window.removeEventListener("afterprint", handleAfterPrint);
                };
            }
        }
    }, [loading, blobUrl, isPdf, navigate]);

    if (loading) return <div className="d-flex justify-content-center align-items-center vh-100">Loading document...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    return (
        <div style={{ width: '100%', height: '100vh', margin: 0, padding: 0, overflow: 'hidden', position: 'relative', backgroundColor: 'white' }}>
            {/* Print-specific styles to hide UI elements during print */}
            <style>
                {`
                    @media print {
                        .no-print { display: none !important; }
                        body, html { margin: 0; padding: 0; height: 100%; overflow: visible; background-color: white; }
                        img { maxWidth: 100%; height: auto; }
                    }
                `}
            </style>

            {/* Manual Back Button for Fallback */}
            <button
                className="btn btn-secondary no-print"
                onClick={() => navigate(-1)}
                style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    zIndex: 1000,
                    opacity: 0.8
                }}
            >
                &larr; Voltar
            </button>

            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {isPdf ? (
                    <iframe
                        src={blobUrl}
                        title="Print Content"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        id="pdf-frame"
                    />
                ) : (
                    <img
                        src={blobUrl}
                        alt="Print Content"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                )}
            </div>
        </div>
    );
};

export default PrintPage;
